'use client';
import React, { useState, useRef, useCallback } from 'react';

type OutputFormat = 'mp3' | 'wav' | 'ogg' | 'm4a' | 'webm';

interface AudioFile {
  file: File;
  url: string;
  size: number;
  format: string;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function getFileFormat(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  const map: Record<string, string> = {
    mp3: 'MP3', wav: 'WAV', m4a: 'M4A', aac: 'AAC',
    ogg: 'OGG', webm: 'WebM', flac: 'FLAC',
  };
  return map[ext] || ext.toUpperCase();
}

const OUTPUT_FORMATS: { value: OutputFormat; label: string; mime: string; desc: string }[] = [
  { value: 'mp3',  label: 'MP3',  mime: 'audio/mpeg',  desc: 'Best compatibility' },
  { value: 'wav',  label: 'WAV',  mime: 'audio/wav',   desc: 'Lossless quality' },
  { value: 'ogg',  label: 'OGG',  mime: 'audio/ogg',   desc: 'Open source' },
  { value: 'm4a',  label: 'M4A',  mime: 'audio/mp4',   desc: 'Apple devices' },
  { value: 'webm', label: 'WebM', mime: 'audio/webm',  desc: 'Web optimized' },
];

const SUPPORTED_INPUT = '.mp3,.wav,.m4a,.aac,.ogg,.webm,.flac';
const SUPPORTED_LABEL = 'MP3, WAV, M4A, AAC, OGG, WebM, FLAC';

export default function AudioConverterTool() {
  const [audioFile, setAudioFile] = useState<AudioFile | null>(null);
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('mp3');
  const [bitrate, setBitrate] = useState<string>('192');
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState('');
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadFile = useCallback((file: File) => {
    const maxSize = 500 * 1024 * 1024;
    if (file.size > maxSize) {
      setError('File is too large. Maximum supported size is 500 MB.');
      return;
    }
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    const supported = ['mp3', 'wav', 'm4a', 'aac', 'ogg', 'webm', 'flac'];
    if (!supported.includes(ext)) {
      setError(`Unsupported file format ".${ext}". Supported formats: ${SUPPORTED_LABEL}.`);
      return;
    }
    setError(null);
    setResultUrl(null);
    setProgress(0);
    setProgressLabel('');
    const url = URL.createObjectURL(file);
    setAudioFile({ file, url, size: file.size, format: getFileFormat(file.name) });
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) loadFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) loadFile(file);
  };

  const handleConvert = async () => {
    if (!audioFile) return;
    setProcessing(true);
    setProgress(0);
    setResultUrl(null);
    setError(null);

    try {
      setProgressLabel('Loading FFmpeg engine…');
      setProgress(5);

      const { FFmpeg } = await import('@ffmpeg/ffmpeg');
      const { fetchFile, toBlobURL } = await import('@ffmpeg/util');

      const ffmpeg = new FFmpeg();

      ffmpeg.on('progress', ({ progress: p }) => {
        const pct = Math.round(20 + p * 70);
        setProgress(pct);
        setProgressLabel(`Converting… ${Math.round(p * 100)}%`);
      });

      setProgressLabel('Loading FFmpeg core (first time may take ~10s)…');
      setProgress(8);

      const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';
      await ffmpeg.load({
        coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
        wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
      });

      setProgressLabel('Reading file…');
      setProgress(18);

      const inputExt = audioFile.file.name.split('.').pop() || 'audio';
      const inputName = `input.${inputExt}`;
      const outputName = `output.${outputFormat}`;

      await ffmpeg.writeFile(inputName, await fetchFile(audioFile.file));

      setProgressLabel('Starting conversion…');
      setProgress(20);

      const args = ['-i', inputName];

      if (outputFormat === 'mp3') {
        args.push('-codec:a', 'libmp3lame', '-b:a', `${bitrate}k`);
      } else if (outputFormat === 'm4a') {
        args.push('-codec:a', 'aac', '-b:a', `${bitrate}k`);
      } else if (outputFormat === 'ogg') {
        args.push('-codec:a', 'libvorbis', '-b:a', `${bitrate}k`);
      } else if (outputFormat === 'webm') {
        args.push('-codec:a', 'libopus', '-b:a', `${bitrate}k`);
      } else if (outputFormat === 'wav') {
        args.push('-codec:a', 'pcm_s16le');
      }

      args.push(outputName);
      await ffmpeg.exec(args);

      setProgressLabel('Preparing download…');
      setProgress(95);

      const data = await ffmpeg.readFile(outputName);
      const mime = OUTPUT_FORMATS.find(f => f.value === outputFormat)?.mime || 'audio/mpeg';
      const blob = new Blob([data], { type: mime });
      const url = URL.createObjectURL(blob);
      setResultUrl(url);
      setResultSize(blob.size);
      setProgress(100);
      setProgressLabel('Done!');
    } catch (err) {
      console.error(err);
      setError(
        'Conversion failed. This may happen if the file is corrupted or the format combination is not supported. Try a different output format.'
      );
    } finally {
      setProcessing(false);
    }
  };

  const handleDownload = () => {
    if (!resultUrl || !audioFile) return;
    const baseName = audioFile.file.name.replace(/\.[^.]+$/, '');
    const a = document.createElement('a');
    a.href = resultUrl;
    a.download = `${baseName}_converted.${outputFormat}`;
    a.click();
  };

  const handleReset = () => {
    if (audioFile?.url) URL.revokeObjectURL(audioFile.url);
    if (resultUrl) URL.revokeObjectURL(resultUrl);
    setAudioFile(null);
    setResultUrl(null);
    setError(null);
    setProgress(0);
    setProgressLabel('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="space-y-5">
      {/* Error Banner */}
      {error && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-100 rounded-xl p-4">
          <svg className="w-5 h-5 text-red-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
          <div className="flex-1">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600 transition-colors shrink-0">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Upload Area */}
      {!audioFile ? (
        <div
          className={`rounded-2xl border-2 border-dashed p-10 text-center cursor-pointer transition-all duration-200 ${
            isDragging
              ? 'border-amber-400 bg-amber-50 scale-[1.01]'
              : 'border-gray-200 bg-gray-50 hover:border-amber-300 hover:bg-amber-50/40'
          }`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          role="button"
          aria-label="Upload audio file"
        >
          <div className="w-16 h-16 rounded-2xl bg-amber-50 border border-amber-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 9l10.5-3m0 6.553v3.75a2.25 2.25 0 01-1.632 2.163l-1.32.377a1.803 1.803 0 11-.99-3.467l2.31-.66a2.25 2.25 0 001.632-2.163zm0 0V2.25L9 5.25v10.303m0 0v3.75a2.25 2.25 0 01-1.632 2.163l-1.32.377a1.803 1.803 0 01-.99-3.467l2.31-.66A2.25 2.25 0 009 15.553z" />
            </svg>
          </div>
          <p className="font-semibold text-gray-800 mb-1 text-base">Upload Audio File</p>
          <p className="text-gray-500 text-sm mb-3">Drag & drop or click to browse</p>
          <div className="inline-flex flex-wrap justify-center gap-1.5">
            {['MP3', 'WAV', 'M4A', 'AAC', 'OGG', 'WebM', 'FLAC'].map(fmt => (
              <span key={fmt} className="px-2 py-0.5 bg-white border border-gray-200 rounded-md text-xs text-gray-500 font-medium">
                {fmt}
              </span>
            ))}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept={SUPPORTED_INPUT}
            className="hidden"
            onChange={handleFileChange}
          />
        </div>
      ) : (
        <div className="space-y-4">
          {/* Original File Info Card */}
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
            <p className="text-xs font-semibold text-amber-700 uppercase tracking-wider mb-3">Original File</p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
                <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 9l10.5-3m0 6.553v3.75a2.25 2.25 0 01-1.632 2.163l-1.32.377a1.803 1.803 0 11-.99-3.467l2.31-.66a2.25 2.25 0 001.632-2.163zm0 0V2.25L9 5.25v10.303m0 0v3.75a2.25 2.25 0 01-1.632 2.163l-1.32.377a1.803 1.803 0 01-.99-3.467l2.31-.66A2.25 2.25 0 009 15.553z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-800 text-sm truncate">{audioFile.file.name}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-amber-200 text-amber-800 text-xs font-bold">
                    {audioFile.format}
                  </span>
                  <span className="text-gray-500 text-xs">{formatBytes(audioFile.size)}</span>
                </div>
              </div>
              <button
                onClick={handleReset}
                className="text-gray-400 hover:text-red-500 transition-colors p-1.5 rounded-lg hover:bg-red-50"
                aria-label="Remove file"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Large file warning */}
          {audioFile.size > 50 * 1024 * 1024 && (
            <div className="flex items-start gap-2 bg-yellow-50 border border-yellow-100 rounded-xl p-3">
              <svg className="w-4 h-4 text-yellow-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
              <p className="text-yellow-700 text-xs leading-relaxed">
                Large audio files may take longer because processing happens entirely in your browser.
              </p>
            </div>
          )}

          {/* Before: Audio Preview */}
          <div className="bg-gray-50 border border-gray-100 rounded-xl p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              🎵 Preview — Before Conversion
            </p>
            <audio src={audioFile.url} controls className="w-full" style={{ height: '40px' }} />
          </div>

          {/* Output Format Selector */}
          <div>
            <p className="text-sm font-semibold text-gray-700 mb-2">Output Format</p>
            <div className="grid grid-cols-5 gap-2">
              {OUTPUT_FORMATS.map((fmt) => (
                <button
                  key={fmt.value}
                  onClick={() => setOutputFormat(fmt.value)}
                  className={`py-2.5 rounded-xl text-sm font-bold border transition-all duration-150 flex flex-col items-center gap-0.5 ${
                    outputFormat === fmt.value
                      ? 'bg-amber-500 text-white border-amber-500 shadow-sm'
                      : 'bg-white text-gray-700 border-gray-200 hover:border-amber-300 hover:bg-amber-50/50'
                  }`}
                >
                  <span>{fmt.label}</span>
                  <span className={`text-[10px] font-normal leading-tight ${outputFormat === fmt.value ? 'text-amber-100' : 'text-gray-400'}`}>
                    {fmt.desc}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Bitrate (not for WAV) */}
          {outputFormat !== 'wav' && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-gray-700">Bitrate</p>
                <span className="text-amber-600 font-bold text-sm bg-amber-50 border border-amber-100 px-2.5 py-0.5 rounded-lg">
                  {bitrate} kbps
                </span>
              </div>
              <div className="flex gap-2 flex-wrap">
                {['64', '96', '128', '192', '256', '320'].map((b) => (
                  <button
                    key={b}
                    onClick={() => setBitrate(b)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                      bitrate === b
                        ? 'bg-amber-500 text-white border-amber-500' :'bg-white text-gray-600 border-gray-200 hover:border-amber-300'
                    }`}
                  >
                    {b}k
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-1.5">
                {outputFormat === 'mp3' ? '128k = standard, 192k = good quality, 320k = best quality' :
                 outputFormat === 'ogg'? '128k = standard, 192k = high quality' : '128k = standard, 192k = high quality'}
              </p>
            </div>
          )}

          {/* Progress Bar */}
          {processing && (
            <div>
              <div className="flex justify-between text-xs text-gray-500 mb-1.5">
                <span className="font-medium">{progressLabel}</span>
                <span className="font-bold text-amber-600">{progress}%</span>
              </div>
              <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-amber-400 to-amber-600 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-xs text-gray-400 mt-1.5 text-center">
                Processing in your browser — please keep this tab open
              </p>
            </div>
          )}

          {/* Result Card — After Conversion */}
          {resultUrl && (
            <div className="bg-green-50 border border-green-100 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                  <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-green-800 text-sm">Conversion complete!</p>
                  <p className="text-green-600 text-xs">
                    {audioFile.format} → {outputFormat.toUpperCase()} · {formatBytes(resultSize)}
                  </p>
                </div>
              </div>

              {/* After: Audio Preview */}
              <div>
                <p className="text-xs font-semibold text-green-700 uppercase tracking-wider mb-2">
                  🎵 Preview — After Conversion
                </p>
                <audio src={resultUrl} controls className="w-full" style={{ height: '40px' }} />
              </div>

              {/* File size comparison */}
              <div className="grid grid-cols-2 gap-2 text-center">
                <div className="bg-white rounded-lg p-2 border border-green-100">
                  <p className="text-xs text-gray-500 mb-0.5">Original</p>
                  <p className="font-bold text-gray-700 text-sm">{formatBytes(audioFile.size)}</p>
                  <p className="text-xs text-gray-400">{audioFile.format}</p>
                </div>
                <div className="bg-white rounded-lg p-2 border border-green-100">
                  <p className="text-xs text-gray-500 mb-0.5">Converted</p>
                  <p className="font-bold text-green-700 text-sm">{formatBytes(resultSize)}</p>
                  <p className="text-xs text-gray-400">{outputFormat.toUpperCase()}</p>
                </div>
              </div>

              <button
                onClick={handleDownload}
                className="w-full py-3 bg-green-600 hover:bg-green-700 active:bg-green-800 text-white font-semibold rounded-xl transition-colors text-sm flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                </svg>
                Download {outputFormat.toUpperCase()} File
              </button>

              <button
                onClick={handleReset}
                className="w-full py-2 text-gray-500 hover:text-gray-700 text-sm font-medium transition-colors"
              >
                Convert another file
              </button>
            </div>
          )}

          {/* Convert Button */}
          {!resultUrl && (
            <button
              onClick={handleConvert}
              disabled={processing}
              className="w-full py-3.5 bg-amber-500 hover:bg-amber-600 active:bg-amber-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors text-sm flex items-center justify-center gap-2"
            >
              {processing ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Converting…
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                  </svg>
                  Convert to {outputFormat.toUpperCase()}
                </>
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
